import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { MarkovState } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import {
    PRM_MARKOV_MODEL,
    StoreType,
    MarkovStateFilters,
    MarkovStartvectorStateList,
    MarkovStartvectorStateCalculated,

    LoadAllMarkovStartvectorStates,
    DeleteMarkovStartvectorState,
    SortMarkovStates,
    MarkovExpressionCalc,

    setMarkovStartvectorStateList,
    setMarkovStartvectorStateIds,
    setMarkovStartvectorStateCalculatedExpressions,
    setMarkovStartvectorStateSort,
    setMarkovStartvectorStateFilters,
    addModal,
} from "client/store"

import {
    Container,
    Loader,
    PageForm,
    FormGroup,
    Label,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import MarkovStateSearch from "client/pages/search/markovStateSearch"
import EditStartvectorState from "client/pages/markov/startvectorState/edit"
import Columns from "./columns"


const List = () => {
    const modelStateList = useSelector((state: StoreType) => state.markovState.stateList)
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const startvectorSelected = useSelector((state: StoreType) => state.markovStartvector.startvectorSelected)

    const startvectorStateList = useSelector((state: StoreType) => state.markovStartvectorState.stateList)
    const stateIds = useSelector((state: StoreType) => state.markovStartvectorState.stateIds)
    const sortColumn = useSelector((state: StoreType) => state.markovStartvectorState.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovStartvectorState.sortOrder)
    const filters = useSelector((state: StoreType) => state.markovStartvectorState.filters)
    const reloadList = useSelector((state: StoreType) => state.markovStartvectorState.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllMarkovStartvectorStates(startvectorSelected && startvectorSelected.id).then((res) => {
            dispatch(setMarkovStartvectorStateList(res))
            setLoadState(IDLE)
        })
    }, [startvectorSelected, reloadList])

    useEffect(() => {
        if (
            !startvectorStateList || !Object.values(startvectorStateList).length
            || !variableList || !Object.values(variableList).length
        ) return
        const calculated: MarkovStartvectorStateCalculated = {}
        Object.values(startvectorStateList).forEach((startvectorState) => {
            if (!startvectorState.expression.length) return
            const calcResult = MarkovExpressionCalc(null, startvectorState.expression, variableList, lang)
            if (calcResult.error) {
                calculated[startvectorState.stateId] = calcResult.error
            } else {
                calculated[startvectorState.stateId] = calcResult.value
            }
        })
        dispatch(setMarkovStartvectorStateCalculatedExpressions(calculated))
    }, [startvectorStateList, variableList])

    useEffect(() => {
        const sortedIds = SortMarkovStates(Object.values(modelStateList), sortColumn, sortOrder, filters)
        const sortedIdsFiltered = sortedIds.filter((id) => !!startvectorStateList[id])
        dispatch(setMarkovStartvectorStateIds(sortedIdsFiltered))
    }, [modelStateList, startvectorStateList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MarkovState, order) => {
        dispatch(setMarkovStartvectorStateSort(column, order))
    }

    const onFilter = (newFilters: MarkovStateFilters) => {
        dispatch(setMarkovStartvectorStateFilters(newFilters))
    }

    const onDelete: DeleteRow = (state: MarkovState, callback) => {
        DeleteMarkovStartvectorState(state.id, startvectorSelected.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const startvectorStateListCopy: MarkovStartvectorStateList = startvectorStateList ? deepCopy(startvectorStateList) : {}
                delete startvectorStateListCopy[state.id]
                dispatch(setMarkovStartvectorStateList(startvectorStateListCopy))
                dispatch(setMarkovStartvectorStateIds(stateIds.filter((id) => id !== state.id)))
            })
        })
    }

    const columns = Columns()
    const editModelAccess = hasAccess(PRM_MARKOV_MODEL, EDIT)

    return (
        <Container>
            {
                loadState === PENDING
                && <Loader />
            }
            <PageForm noPaddingTop noPaddingBottom>
                <FormGroup>
                    <Label>{lang.states}</Label>
                </FormGroup>
            </PageForm>
            <TableSearchWrapper>
                <MarkovStateSearch
                    id="startvectorStateList"
                    rightOffset={editModelAccess}
                    filters={filters}
                    onFilter={onFilter}
                />
                {
                    editModelAccess && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                key: "addStartvectorState",
                                content: <EditStartvectorState stateId={0} />,
                            }))}
                        >
                            {lang.add}
                        </Button>
                    )
                }
            </TableSearchWrapper>
            <Table
                columns={columns}
                dataList={modelStateList}
                dataIds={stateIds}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                rowSize="sm"
                showPagination
                scrollToTopOnPagination={false}
                onSort={onSort}
                onDelete={onDelete}
            />
        </Container>
    )
}
export default List
